import { AlignLeftOutlined } from "@ant-design/icons";
import {
  useTranslate,
  Authenticated,
  CanAccess,
  useCustom,
  useInfiniteList,
} from "@refinedev/core";
import { Button, Image, Space, Switch, Typography, DatePicker } from "antd";
import { useCallback, useEffect, useMemo, useState, useTransition } from "react";
import { Organization } from "types";
import { Show } from "@refinedev/antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjsBusinessDays from "dayjs-business-days";
import { DetailedBatchStatus, MediaBatchResponse } from "../types";
import BatchScriptList from "../components/BatchScriptList";
import { filterVideos, sortVideos } from "./helpers";
import { SortField, Video } from "./types";
import { BatchListFilters } from "./components/BatchListFilters";
import { DEFAULT_SORT_FIELD } from "./constants";
import { BatchItemsList } from "./components/BatchItemsList";

dayjs.extend(dayjsBusinessDays);
dayjs.extend(customParseFormat);

export const MediaBatchList = ({
  organization,
}: {
  organization?: Organization;
}) => {
  const t = useTranslate();

  const id = "ALL";
  const dateFormat = "YYYY-MM-DD";

  const [videos, setVideos] = useState<Video[]>([]);
  const [showStatus, setShowStatus] = useState(false);
  const [isMatching, startTransition] = useTransition();
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>(DEFAULT_SORT_FIELD);
  const [filterValue, setFilterValue] = useState<string[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const [date, setDate] = useState<dayjs.Dayjs>(
    (dayjs(new Date()) as any).prevBusinessDay()
  );
  const key = date.format(dateFormat);

  const { data, hasNextPage, fetchNextPage, isFetching, isFetchingNextPage } =
    useInfiniteList<MediaBatchResponse>({
      resource: `media/${organization?.id}/batches/${id}/execution/${key}`,
      meta: { cursor: true },
      queryOptions: { enabled: Boolean(organization?.id) },
    });

  const { data: statusData } = useCustom<DetailedBatchStatus>({
    url: `media/${organization?.id}/batches/${id}/execution/${key}/debug`,
    method: "get",
    queryOptions: {
      enabled: Boolean(organization?.id) && showStatus,
    },
  });

  const handleDateChange = (value: dayjs.Dayjs | null) => {
    if (!value) return;
    setDate(value);
  };

  const videoList: Video[] = useMemo(() => {
    return showStatus
      ? ((statusData?.data.medias.map((x) => x.media) as Video[]) ?? [])
      : [
          ...(data?.pages.reduce(
            (acc: Video[], page) => [
              ...acc,
              // the custom cursor-pagination data provider returns the raw
              // MediaBatchResponse body as `data`, not an array (see data-provider.ts)
              ...((page.data as unknown as MediaBatchResponse)
                .medias as Video[]),
            ],
            []
          ) ?? []),
        ];
  }, [data, showStatus, statusData]);

  useEffect(() => {
    startTransition(() => {
      if (search.length === 0) {
        setVideos([
          ...filterVideos([], search, filterValue),
          ...sortVideos(
            filterVideos(videoList, search, filterValue),
            sortField
          ),
        ]);
        return;
      }
      const filtered = filterVideos(videoList, search, filterValue);
      const sorted = sortVideos(filtered, sortField);
      setVideos(sorted);
    });
  }, [data, search, videoList, sortField, filterValue]);

  const videoTypes = [...new Set(videoList.map((x) => x.metadata.type))].filter(
    (x) => x !== undefined
  );

  const toggleDrawer = () => {
    setDrawerVisible(!drawerVisible);
  };

  const handleFetchNextPage = useCallback(() => {
    fetchNextPage().catch((error) => {
      console.error("Failed to fetch next page", error);
    });
  }, [fetchNextPage]);

  return (
    <Authenticated key="media_batches">
      <Show
        title={
          <Space wrap size={"large"}>
            <Typography.Title
              level={4}
              style={{ marginTop: 0, marginBottom: 0 }}
            >
              {t("media.batches.list.videoBuilder")}{" "}
              <Typography.Text style={{ opacity: 0.6 }}>
                {t("media.batches.list.v10")}
              </Typography.Text>
            </Typography.Title>

            <Image
              onError={(e) => ((e.target as any).style.display = "none")}
              onLoad={(e) => ((e.target as any).style.display = "block")}
              width={200}
              height={30}
              alt="logo"
              preview={false}
              src={`/organization/${organization?.id}/${organization?.id}.svg`}
            />
          </Space>
        }
        headerButtons={() => (
          <Space size={"middle"}>
            <CanAccess resource="media_generation_steps" action="show">
              <Switch
                unCheckedChildren={"Status"}
                checkedChildren={"Status"}
                checked={showStatus}
                onClick={() => setShowStatus((value) => !value)}
              />
            </CanAccess>
            <DatePicker
              size="large"
              defaultValue={(dayjs() as any).prevBusinessDay()}
              format={dateFormat}
              onChange={handleDateChange}
            />
            <CanAccess resource="media_generation_steps" action="show">
              <Button
                onClick={toggleDrawer}
                size="large"
                icon={<AlignLeftOutlined />}
              >
                {t("media.batches.list.showScripts")}
              </Button>
            </CanAccess>
          </Space>
        )}
      >
        {organization && (
          <BatchScriptList
            organization={organization}
            date={key}
            mediaTypes={videoTypes}
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
          />
        )}
        <BatchListFilters
          allVideos={videoList}
          videos={videos}
          isFetching={isFetching}
          isMatching={isMatching}
          setSearch={setSearch}
          setFilterValue={setFilterValue}
          setSortField={setSortField}
          showStatus={showStatus}
        />
        <BatchItemsList
          videos={videos}
          showStatus={showStatus}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={handleFetchNextPage}
        />
      </Show>
    </Authenticated>
  );
};
